import { IJitsiMeetExternalApi } from "@jitsi/react-sdk/lib/types";
import { create } from "zustand";

type CallInfo = {
  currentCallId: string;
  token: string;
  subject: string;
};

type CallState = {
  callInfo: CallInfo | null;
  showConferenceUI: boolean;
  joinedCall: string | null;
  activeCallIds: string[];
  callMinimized: boolean;
  callEnding: boolean;

  hasActiveCallInP2Ps: boolean;
  hasActiveCallInGroups: boolean;

  micMuted: boolean;
  videoMuted: boolean;
  apiRef: IJitsiMeetExternalApi | null;

  // Orion chat side panel (in-call chat), mirrors the moderation panel's open-state pattern.
  // The panel shows the Orion conversation for `callInfo.currentCallId` (the room IS the chat id).
  isOrionChatPanelOpen: boolean;
};

type Actions = {
  setHasActiveCallInP2Ps: (active: boolean) => void;
  setHasActiveCallInGroups: (active: boolean) => void;
  setCallMinimized: (minimized: boolean) => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
  hangup: () => void;
  setCallEnding: (value: boolean) => void;
  setShowConferenceUI: (show: boolean) => void;
  setJoinedCall: (value: string | null) => void;
  setActiveCallIds: (ids: string[]) => void;
  setUserDisconnected: () => void;
  setCallInfo: (info: CallInfo | null) => void;
  setApiRef: (api: IJitsiMeetExternalApi) => void;
  isInActiveCallList: (roomId: string) => boolean;
  removeFromActiveCalls: (roomId: string) => void;
  setOrionChatPanelOpen: (open: boolean) => void;
  toggleOrionChatPanel: () => void;
  resetCallState: () => void;
};

const initialState: CallState = {
  callInfo: null,
  showConferenceUI: false,
  joinedCall: null,
  activeCallIds: [],
  callMinimized: false,
  callEnding: false,

  hasActiveCallInP2Ps: false,
  hasActiveCallInGroups: false,

  micMuted: false,
  videoMuted: false,
  apiRef: null,

  isOrionChatPanelOpen: false,
};

export const useCallState = create<CallState & Actions>()((set, get) => ({
  // State
  ...initialState,

  // Actions
  setHasActiveCallInP2Ps(active) {
    set({
      hasActiveCallInP2Ps: active,
    });
  },
  setHasActiveCallInGroups(active) {
    set({
      hasActiveCallInGroups: active,
    });
  },
  setCallMinimized(minimized) {
    set({
      callMinimized: minimized,
    });
  },
  toggleAudio() {
    const api = get().apiRef;
    api?.executeCommand("toggleAudio");
  },
  toggleVideo() {
    const api = get().apiRef;
    api?.executeCommand("toggleVideo");
  },
  hangup() {
    const api = get().apiRef;
    api?.executeCommand("hangup");
    get().setUserDisconnected();
  },
  setApiRef(api) {
    set({ apiRef: api });
  },
  setActiveCallIds(ids) {
    set({ activeCallIds: ids || [] });
  },
  setShowConferenceUI(show: boolean) {
    set({ showConferenceUI: show });
  },
  setJoinedCall(value: string | null) {
    set({ joinedCall: value });
  },
  setCallEnding(value: boolean) {
    set({ callEnding: value });
  },
  setUserDisconnected() {
    set({
      callInfo: null,
      joinedCall: null,
      showConferenceUI: false,
      callMinimized: false,
      // Leaving/ending the call also closes the in-call Orion chat panel.
      isOrionChatPanelOpen: false,
    });
  },
  setCallInfo(info: CallInfo | null) {
    set((state) => ({
      callInfo: info ? { ...state.callInfo, ...info } : null,
    }));
  },
  isInActiveCallList(roomId: string) {
    return get().activeCallIds.includes(roomId);
  },
  removeFromActiveCalls(roomId: string) {
    const newIds = get().activeCallIds.filter((i) => i !== roomId);
    if (newIds.length > 0) {
      set({ activeCallIds: newIds });
    } else {
      set({
        activeCallIds: newIds,
        hasActiveCallInGroups: false,
        hasActiveCallInP2Ps: false,
      });
    }
  },
  setOrionChatPanelOpen(open: boolean) {
    set({ isOrionChatPanelOpen: open });
  },
  toggleOrionChatPanel() {
    set((state) => ({ isOrionChatPanelOpen: !state.isOrionChatPanelOpen }));
  },
  resetCallState() {
    set(initialState);
  },
}));
